import { useEffectWithController } from '@carvana/merch-react-hooks';
import { useContext, useRef } from 'react';
import { useResolvedOnServer } from '~/context/common/DeferredResolutionContext';
import { useUserId, useUserLocation } from '~/context/common/UserContext';
import DiscountContext from '~/context/vdp/DiscountContext';
import useSkipFirstFetchIfResolvedOnServer from '~/hooks/common/useSkipFirstFetchIfResolvedOnServer';
import fetchDiscounts from '~/services/vdp/fetchDiscounts';
import { generateErrorLogger } from '~/utilities/loggers';

export default function useGetDiscounts() {
  const {
    discounts: { setData, setError, setLoading },
    fetchDiscountsFlag
  } = useContext(DiscountContext);
  const { discounts: discountsResolvedOnServer } = useResolvedOnServer();
  const shouldSkipFetch = useSkipFirstFetchIfResolvedOnServer(
    discountsResolvedOnServer
  );
  const lastFetchFlag = useRef(fetchDiscountsFlag);
  const userId = useUserId();
  const { state } = useUserLocation();

  useEffectWithController(
    controller => {
      const wasTriggered = fetchDiscountsFlag !== lastFetchFlag.current;
      lastFetchFlag.current = fetchDiscountsFlag;

      // A miss-triggered fetch always runs, even during the server-resolved skip window.
      if (shouldSkipFetch() && !wasTriggered) {
        return;
      }

      setLoading();

      fetchDiscounts(state, userId, controller)
        .then(res => setData(res.data.content?.discounts ?? []))
        .catch(
          generateErrorLogger(
            'Get discounts',
            {},
            { callback: setError, controller }
          )
        );
    },
    [
      fetchDiscountsFlag,
      setData,
      setError,
      setLoading,
      shouldSkipFetch,
      state,
      userId
    ]
  );
}
